/**
 * The real-account card (TMMAPP-0151 §D 11–12; visual contract v2 §6, P:470–482): one shape for
 * every account and holding. `surface`, radius 18 (the card radius), a 1 px `line` border, padding 18,
 * with the whole card opening its page (the name is the link, stretched over the card). In order:
 * the name (15 / 600) with its permission chip and the kebab; the meta line (12.5, the label ink);
 * the micro tags and the connection status; the balance in `figure` (the heading face, tabular;
 * `link`, or `moneyNegative` below zero); a 6 px filled bar (`ProgressTrack`, TMMAPP-0156 item 4)
 * where the card has one; its line (12.5); then the extras and the footer. The border steps up to `lineStrong` under the pointer.
 *
 * TMMAPP-0120: an owner's sortable card carries the six-dot `handle`, and a bank-linked card its
 * connection `status`. TMMAPP-0154 item 10: the handle (`ReorderHandle corner="end"`) floats in the
 * top inline-end corner on hover or focus and adds no row, so every card keeps the same padding.
 */
import { useState, type ReactNode } from 'react';
import { Box, IconButton, Link, Typography, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { KebabIcon } from '@/components/icons';
import { ProgressTrack, type ProgressTone } from '@/components/ProgressTrack';
import { REVEAL_REORDER_HANDLE_SX } from '@/components/reorderHandleReveal';
import type { PrivacyChipContent } from '@/shell/privacy';
import { ObjectMenu } from '@/shell/OverflowMenu';
import { ToneChip } from '@/shell/PrivacyChip';
import { resolveTokens } from '@/theme/theme';
import { labelCanvasSx } from '@/theme/textLabel';

interface CardMenuProps {
  /** The menu header: the object's name. */
  title: string;
  /** The button's accessible name. */
  label: string;
  children: (close: () => void) => ReactNode;
}

/** The card kebab: 28px, `text2`, hover `line` fill, opening the `card` object menu. */
export function CardMenu({ title, label, children }: CardMenuProps) {
  const tokens = resolveTokens(useTheme());
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => setAnchor(null);
  return (
    <>
      <IconButton
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: '0 0 28px',
          width: 28,
          height: 28,
          p: 0,
          marginBlockStart: '-4px',
          marginInlineEnd: '-6px',
          color: tokens.text2,
        }}
      >
        <KebabIcon size={16} />
      </IconButton>
      <ObjectMenu title={title} anchorEl={anchor} open={anchor !== null} onClose={close}>
        {children(close)}
      </ObjectMenu>
    </>
  );
}

interface RealAccountCardProps {
  name: string;
  /** Where the card goes (an account page, a holding page). */
  to: string;
  /** The permission chip; absent where the grants aren't served. */
  chip?: PrivacyChipContent | null | undefined;
  /** "Institution ···· last4 · subtype". */
  meta: ReactNode;
  /** Bare micro tags (Unmanaged, Granted, Closed, Disposed). */
  tags?: readonly string[];
  /** The balance, in `figure`. */
  balance: ReactNode;
  /** The card's bar: the filled share, 0–1; absent for no bar. */
  bar?: number | null | undefined;
  /** The bar's accessible name. */
  barLabel?: string | undefined;
  /** The bar's fill: a loan paid is `loan`, the sage `successFg` (TMMAPP-0157 item 5); else the brand. */
  barTone?: ProgressTone | undefined;
  /** The line under the balance (or the bar). */
  line?: ReactNode;
  /** D1 extras after the prototype's own lines (equity, financed by, marked as of, set starting balance). */
  extras?: ReactNode;
  footer?: ReactNode;
  /** The kebab; absent for a card the viewer cannot act on. */
  menu?: ReactNode;
  dim?: boolean;
  /** The corner reorder handle (pattern 15). */
  handle?: ReactNode;
  /** The connection status (TMMAPP-0120 item 6). */
  status?: ReactNode;
  /** "{n} to review", a link to the account's Items to review (TMMAPP-0159 §F 33). */
  review?: ReactNode;
}

export function RealAccountCard({
  name,
  to,
  chip,
  meta,
  tags = [],
  balance,
  bar,
  barLabel,
  barTone,
  line,
  extras,
  footer,
  menu,
  dim = false,
  handle,
  status,
  review,
}: RealAccountCardProps) {
  const tokens = resolveTokens(useTheme());
  const label = tokens.textLabel('surface');
  const has = (node: ReactNode) => node !== undefined && node !== null && node !== '';
  return (
    <Box
      data-account-card=""
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        height: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        p: '18px',
        bgcolor: tokens.surface,
        color: tokens.text,
        border: '1px solid',
        borderColor: tokens.line,
        borderRadius: `${tokens.radius.card}px`,
        ...labelCanvasSx(tokens, 'surface'),
        '&:hover': { borderColor: tokens.lineStrong },
        ...REVEAL_REORDER_HANDLE_SX,
        // Every control on the card sits above the stretched link.
        '& button:not([data-reorder-handle]), & a:not([data-card-link]), & [data-card-control]': {
          position: 'relative',
          zIndex: 1,
        },
      }}
    >
      {handle}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Link
          component={RouterLink}
          to={to}
          underline="none"
          data-card-link=""
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            fontSize: 15,
            lineHeight: '21px',
            fontWeight: 600,
            color: dim ? tokens.text2 : tokens.text,
            overflowWrap: 'anywhere',
            // The whole card opens the page; keyboard focus rings the card.
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: `${tokens.radius.card}px`,
            },
            '&:focus-visible': { outline: 'none' },
            '&:focus-visible::after': {
              outline: `2px solid ${tokens.focusRing}`,
              outlineOffset: 2,
            },
          }}
        >
          {name}
        </Link>
        {chip !== undefined && chip !== null && <ToneChip {...chip} />}
        {menu}
      </Box>
      {has(meta) && (
        <Typography
          component="div"
          data-card-meta=""
          sx={{ fontSize: 12.5, lineHeight: '18px', color: label, overflowWrap: 'anywhere' }}
        >
          {meta}
        </Typography>
      )}
      {(tags.length > 0 || has(status) || has(review)) && (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          {review}
          {tags.map((tag) => (
            <Typography key={tag} variant="micro" component="span" sx={{ color: tokens.text2 }}>
              {tag}
            </Typography>
          ))}
          {has(status) && (
            <Box data-card-control="" sx={{ marginInlineStart: 'auto', minWidth: 0 }}>
              {status}
            </Box>
          )}
        </Box>
      )}
      <Box data-card-balance="" sx={{ mt: '4px', '& .MuiTypography-root': { textAlign: 'start' } }}>
        {balance}
      </Box>
      {bar !== undefined && bar !== null && (
        <ProgressTrack value={bar * 100} tone={barTone} label={barLabel} />
      )}
      {has(line) && (
        <Typography
          component="div"
          data-card-line=""
          sx={{ fontSize: 12.5, lineHeight: '18px', color: label, overflowWrap: 'anywhere' }}
        >
          {line}
        </Typography>
      )}
      {has(extras) && (
        <Box data-card-control="" sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {extras}
        </Box>
      )}
      <Box sx={{ flex: 1 }} />
      {has(footer) && (
        <Typography
          variant="caption"
          component="div"
          sx={{ fontSize: 11, lineHeight: '16px', color: label, overflowWrap: 'anywhere' }}
        >
          {footer}
        </Typography>
      )}
    </Box>
  );
}
